import React, { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useStore } from '../store/useStore';
import { createProject, getProjects } from '../utils/api';
import styles from './CreateProjectModal.module.css';

export default function CreateProjectModal() {
  const {
    setCreateProjectModalOpen,
    setProjects,
    projectTemplates,
    users, currentUser,
  } = useStore();

  const [form, setForm] = useState({
    name: '',
    number: '',
    project_manager: '',
    superintendent: '',
    address: '',
    description: '',
    template_id: '',
  });
  const [invited, setInvited] = useState(new Set(
    currentUser ? [currentUser.id] : []
  ));
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = form.name.trim().length > 0 && !submitting;

  const selectedTemplate = useMemo(
    () => projectTemplates.find(t => t.id === form.template_id) || null,
    [form.template_id, projectTemplates]
  );

  function update(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }
  function toggleMember(uid) {
    setInvited((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  }

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await createProject({
        ...form,
        template_id: form.template_id || undefined,
        member_ids: Array.from(invited),
      });
      const fresh = await getProjects();
      setProjects(fresh);
      toast.success(`Project "${form.name}" created`);
      setCreateProjectModalOpen(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className={styles.backdrop}
      onClick={(e) => e.target === e.currentTarget && setCreateProjectModalOpen(false)}
    >
      <div className={styles.modal}>
        <div className={styles.header}>
          <span className={styles.title}>NEW PROJECT</span>
          <button className={styles.closeBtn} onClick={() => setCreateProjectModalOpen(false)}>×</button>
        </div>

        <div className={styles.body}>
          {/* Template picker */}
          <section className={styles.section}>
            <label className={styles.sectionLabel}>START FROM</label>
            <div className={styles.templateGrid}>
              <button
                type="button"
                className={`${styles.templateCard} ${!form.template_id ? styles.templateActive : ''}`}
                onClick={() => update('template_id', '')}
              >
                <div className={styles.templateName}>Blank</div>
                <div className={styles.templateDesc}>Start from scratch — no disciplines or version sets</div>
              </button>
              {projectTemplates.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`${styles.templateCard} ${form.template_id === t.id ? styles.templateActive : ''}`}
                  onClick={() => update('template_id', t.id)}
                >
                  <div className={styles.templateName}>{t.name}</div>
                  <div className={styles.templateDesc}>{t.description || '—'}</div>
                  <div className={styles.templateMeta}>
                    {(t.definition?.disciplines?.length || 0)} disciplines ·{' '}
                    {(t.definition?.version_sets?.length || 0)} version sets
                  </div>
                </button>
              ))}
            </div>
            {selectedTemplate && (
              <div className={styles.templatePreview}>
                <span className={styles.previewLabel}>Includes:</span>{' '}
                {(selectedTemplate.definition?.disciplines || [])
                  .map(d => d.designator).join(', ') || '—'}
              </div>
            )}
          </section>

          {/* Details */}
          <section className={styles.section}>
            <label className={styles.sectionLabel}>PROJECT DETAILS</label>
            <div className={styles.formGrid}>
              <Field label="Name *" grow>
                <input
                  className={styles.input}
                  value={form.name}
                  onChange={(e) => update('name', e.target.value)}
                  placeholder="Central Tower — Phase 3"
                  autoFocus
                />
              </Field>
              <Field label="Job Number">
                <input
                  className={`${styles.input} ${styles.mono}`}
                  value={form.number}
                  onChange={(e) => update('number', e.target.value)}
                  placeholder="CHR-2025-002"
                />
              </Field>
              <Field label="Project Manager">
                <input
                  className={styles.input}
                  value={form.project_manager}
                  onChange={(e) => update('project_manager', e.target.value)}
                  placeholder="Morgan Delacroix"
                />
              </Field>
              <Field label="Superintendent">
                <input
                  className={styles.input}
                  value={form.superintendent}
                  onChange={(e) => update('superintendent', e.target.value)}
                  placeholder="Jamie Chen"
                />
              </Field>
              <Field label="Address" grow>
                <input
                  className={styles.input}
                  value={form.address}
                  onChange={(e) => update('address', e.target.value)}
                  placeholder="1120 Market St, San Francisco, CA 94103"
                />
              </Field>
              <Field label="Description" grow>
                <textarea
                  className={styles.textarea}
                  rows={2}
                  value={form.description}
                  onChange={(e) => update('description', e.target.value)}
                  placeholder="Short internal description (optional)"
                />
              </Field>
            </div>
          </section>

          {/* Invites */}
          <section className={styles.section}>
            <label className={styles.sectionLabel}>INVITE MEMBERS</label>
            <p className={styles.sectionHint}>
              Only invited members will see this project. You are included by default.
            </p>
            <div className={styles.memberList}>
              {users.map((u) => {
                const isChecked = invited.has(u.id);
                const isSelf = currentUser?.id === u.id;
                return (
                  <label
                    key={u.id}
                    className={`${styles.memberRow} ${isChecked ? styles.memberActive : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      disabled={isSelf}
                      onChange={() => toggleMember(u.id)}
                    />
                    <span className={styles.memberName}>
                      {u.name}
                      {isSelf && <span className={styles.you}> (you)</span>}
                    </span>
                    <span className={styles.memberEmail}>{u.email}</span>
                    <span className={`${styles.memberRole} ${styles['role_' + u.role]}`}>{u.role}</span>
                  </label>
                );
              })}
              {users.length === 0 && (
                <div className={styles.emptyMembers}>No users found.</div>
              )}
            </div>
          </section>
        </div>

        <div className={styles.footer}>
          <button
            className={styles.cancelBtn}
            onClick={() => setCreateProjectModalOpen(false)}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            className={styles.submitBtn}
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitting ? 'Creating…' : 'Create Project'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, grow, children }) {
  return (
    <div className={`${styles.field} ${grow ? styles.fieldGrow : ''}`}>
      <label className={styles.fieldLabel}>{label}</label>
      {children}
    </div>
  );
}
